import { useFirebase } from '@chem-po/firebase-web'
import { PopUpMessageContext, useAuth } from '@hb/react'
import { PhoneAuthProvider, PhoneMultiFactorGenerator, PhoneMultiFactorInfo } from 'firebase/auth'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { signInCallback } from '../../../backend/auth/signIn'

export const usePhoneVerify = (factor: PhoneMultiFactorInfo, automaticallySendSmsCode: boolean) => {
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState('')
  const [sendingCode, setSendingCode] = useState(false)
  const [verificationId, setVerificationId] = useState('')

  const { auth } = useFirebase()

  // const [{ automaticallySendSmsCode }, setCookie] = useCookies(['automaticallySendSmsCode'])

  const initSendCode = useRef(automaticallySendSmsCode)

  const { processResponse, showSuccess, showError, showInfo } = useContext(PopUpMessageContext)

  const sendCode = useCallback(() => {
    const session = useAuth.getState().multiFactorResolver?.session
    if (!session) {
      processResponse({ error: 'Error - No session found' })
      return
    }
    setSendingCode(true)
    const phoneAuthProvider = new PhoneAuthProvider(auth)
    const verifier = useAuth.getState().recaptchaVerifier
    if (!verifier) {
      setSendingCode(false)
      processResponse({ error: 'Error - No recaptcha verifier found' })
      return
    }
    phoneAuthProvider
      .verifyPhoneNumber(
        {
          multiFactorHint: factor,
          session,
        },
        verifier,
      )
      .then(v => {
        setVerificationId(v)
      })
      .catch(e => {
        console.error(e)
        console.error(e.code)
        processResponse({ error: e.message || 'Error sending code' })
      })
      .finally(() => setSendingCode(false))
  }, [auth, factor, processResponse])

  const initSendCodeFunc = useRef(sendCode)
  useEffect(() => {
    if (initSendCode.current) {
      initSendCodeFunc.current()
    }
  }, [])

  const handleVerify = useCallback(() => {
    setVerifying(true)
    setError('')
    const resolver = useAuth.getState().multiFactorResolver
    const cred = PhoneAuthProvider.credential(verificationId, code)
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
    resolver
      ?.resolveSignIn(multiFactorAssertion)
      .then(credential => {
        setVerifying(false)
        setVerificationId('')
        setCode('')
        signInCallback(credential.user, showSuccess, showError, showInfo)
      })
      .catch(e => {
        setVerifying(false)
        setError(e.message || 'An error occurred')
      })
  }, [code, verificationId, showSuccess, showError, showInfo])

  return {
    code,
    setCode,
    verifying,
    error,
    sendingCode,
    verificationId,
    handleVerify,
    sendCode,
  }
}
